import { useEffect, useRef } from 'react';
import trackingConsole from '../../utils/trackingConsole';
import { useDispatch, useStore } from 'react-redux';
import { useLocation } from 'react-router';
import { isBooleanTrue } from '../../utils/objectUtils';
import { isBundleScopedRoute } from '../../utils/funnelRouteUtils';
import { getOrchestrator, initOrchestratorVersionListeners, refreshBundleScopedCollections } from '../../redux/ducks/orchestrator';

// the funnel, checkout and encounter routes render from a program bundle and deliberately skip full
// hydration - loading the ~10MB catalog underneath them replaces every collection and re-renders the
// funnel on each step, checkout included. Lives inside the Router so the first navigation away is
// what hydrates, which is also the first moment anything needs the full catalog.
const OrchestratorHydrator = () => {
	//#region useDispatch

	const dispatch = useDispatch();

	//#endregion
	//#region useStore

	const store = useStore();

	//#endregion
	//#region useLocation

	const location = useLocation();

	//#endregion
	//#region useRefs

	// hydration runs at most once per page load; the listeners it attaches detach on unmount
	const hydrationStartedRef = useRef(false);
	const unsubscribeRef = useRef(null);

	//#endregion
	//#region useEffects

	useEffect(() => {
		if (isBooleanTrue(hydrationStartedRef.current) || isBundleScopedRoute(location.pathname)) {
			return;
		}

		hydrationStartedRef.current = true;

		// a normal boot already loaded everything and attached its own listeners
		if (store.getState()?.orchestrator?.bootstrapScope === 'FULL') {
			return;
		}

		(async () => {
			try {
				// the bundle left program-scoped products/variants/surveys under fresh timestamps, so
				// the early-return guard serves those partial lists until they are forced
				await dispatch(refreshBundleScopedCollections());
				await dispatch(getOrchestrator());
				unsubscribeRef.current = initOrchestratorVersionListeners(store);
			} catch (error) {
				trackingConsole.error('Error hydrating the full orchestrator after leaving the bundle-scoped routes', error);
			}
		})();
	}, [location.pathname, dispatch, store]);

	useEffect(() => {
		return () => {
			if (typeof unsubscribeRef.current === 'function') {
				unsubscribeRef.current();
				unsubscribeRef.current = null;
			}
		};
	}, []);

	//#endregion

	return null;
};

export default OrchestratorHydrator;
